import { useContext, useRef } from 'react'
import { X } from 'lucide-react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json'

/**
 * Generic "active filters" chip bar with one-click removal.
 *
 * Reads a selection map from the store (e.g. `selectedFilters`) and renders a
 * removable chip per active value, driven by a declarative `descriptors`
 * list. Removing a chip clears that slot in the store; an optional
 * "clear all" chip resets every declared slot. Doing this in YAML would hit
 * the "no if/else" wall (a chip must appear only when its filter is set) —
 * the primitive resolves the visible set in JS instead.
 *
 * The refetch is the YAML `actions:`, but bound to the custom **`activefilterschange`**
 * event (NOT `click`) : the component clears the store on a chip/clear-all press
 * and THEN dispatches that event, so the actions fire only on a real change — a
 * click in the wrapper's empty space (flex gaps, end-of-row) triggers nothing.
 * Wire it with a merge key so the `click` anchors stay reusable :
 *   actions:
 *     - { <<: *resetFrom, on: activefilterschange }
 *     - { <<: *esRefresh, on: activefilterschange }
 *
 * `descriptors` entries:
 *   - { key, label, type: list, labelMap } → one chip per array value
 *       (labelMap maps the raw value → display label, e.g. machine name → human)
 *   - { key, label, type: tristate, labelMap } → reads the backend
 *       `{any,all,none}` shape : one INCLUDE chip per value in `any`+`all`, one
 *       EXCLUDE chip per value in `none`. Exclude chips are visually distinct
 *       (rose, struck-through) and prefixed "≠ " so they read without colour.
 *       Removing a chip drops that value from its bucket only.
 *   - { key, label, type: scalar, labelMap } → one chip when non-empty ("label: value")
 *   - { key, label, type: range,    → one chip when {gte|gt|lte|lt} set. If the
 *       prefix, suffix }              stored range carries a `_label` key, it is
 *                                     used verbatim as the chip text (e.g. an
 *                                     "Avant 2020" year tranche); otherwise the
 *                                     bounds are formatted ("label: gte – lte").
 *
 * `chipClass` / `clearAllClass` override the per-chip / clear-all button classes.
 *
 * YAML usage:
 *   - type: ActiveFilterChips
 *     dataLocation: ~~.selectedFilters
 *     clearAllLabel: 'Effacer tout'
 *     descriptors:
 *       - { key: by_brand,    label: 'Marque',   type: list }
 *       - { key: by_condition,label: 'État',     type: scalar }
 *       - { key: by_price,    label: 'Prix',     type: range, suffix: ' €' }
 *     actions:
 *       - { what: setData, on: click, path: ~~.pagination.from, value: 0 }
 *       - { what: submitData, on: click, ... }
 */

const emptyFor = (type) =>
  type === 'list' ? [] : (type === 'range' || type === 'tristate') ? {} : ''

export const ActiveFilterChips = ({ props, path, datafield }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const containerRef = useRef(null)

  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const descriptors = resolve(props.descriptors)
  const clearAllLabel = resolve(props.clearAllLabel)
  const chipClass = resolve(props.chipClass) ??
    'flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-200 hover:bg-indigo-100 transition-colors'
  // Distinct, NON-colour-only style for excluded (NOT) tri-state values : rose
  // tone + struck-through label + a "≠ " prefix added to the chip text.
  // `text-rose-700` (not 600) keeps the contrast AA on both bg-rose-50 (5.72:1)
  // and the hover bg-rose-100 (5.24:1) — rose-600 on rose-50 was 4.28:1 (< 4.5).
  const excludeChipClass = resolve(props.excludeChipClass) ??
    'flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 transition-colors line-through'
  const clearAllClass = resolve(props.clearAllClass) ??
    'text-xs font-semibold px-2.5 py-1 rounded-full border border-red-200 text-red-500 hover:bg-red-50 transition-colors'
  if (!Array.isArray(descriptors)) return null

  const { formData, formDataPath } = propsDataLocationToPathAndValue({
    currentPath: path,
    datafield,
    dataLocation: props.dataLocation,
    defaultValue: {},
    globalDataContext,
    templateContext,
  })
  const selection = formData && typeof formData === 'object' ? formData : {}

  const rangeLabel = (d, v) => {
    const p = d.prefix ?? ''
    const s = d.suffix ?? ''
    const lo = v.gte != null ? `${p}${v.gte}${s}` : ''
    const hi = v.lte != null ? `${p}${v.lte}${s}` : ''
    if (lo && hi) return `${lo} – ${hi}`
    if (lo) return `≥ ${lo}`
    if (hi) return `≤ ${hi}`
    return ''
  }

  // Build the flat list of chips to render.
  const chips = []
  for (const d of descriptors) {
    const v = selection[d.key]
    const slotPath = `${formDataPath}.${d.key}`
    // `labelMap` may be inline OR a template ref to a store label table (e.g.
    // `~~.facetOptions.aggregations.by_project_labels`). The descriptors array is
    // only resolved one level deep, so a string here is still unresolved — resolve
    // it now so the global "active filters" bar can reuse the `<agg>_labels`.
    const rawLabelMap = typeof d.labelMap === 'string' ? resolve(d.labelMap) : d.labelMap
    const labelMap = (rawLabelMap && typeof rawLabelMap === 'object') ? rawLabelMap : {}
    const mapLabel = (val) => (labelMap[val] != null ? String(labelMap[val]) : String(val))
    if (d.type === 'list') {
      if (Array.isArray(v)) {
        v.forEach((item) => {
          chips.push({
            id: `${d.key}:${item}`,
            label: mapLabel(item),
            remove: () => globalDataContext.updateData(v.filter((x) => x !== item), slotPath),
          })
        })
      }
    } else if (d.type === 'tristate') {
      // Backend `{any,all,none}` shape : included (any+all) → normal chips,
      // excluded (none) → struck-through "≠ " chips. Removing a chip drops the
      // value from its bucket only, leaving the rest of the model intact.
      const model = v && typeof v === 'object' && !Array.isArray(v) ? v : {}
      const listAt = (k) => (Array.isArray(model[k]) ? model[k] : [])
      const removeFrom = (bucket, item) => () => {
        const next = { ...model, [bucket]: listAt(bucket).filter((x) => x !== item) }
        globalDataContext.updateData(next, slotPath)
      }
      ;['any', 'all'].forEach((bucket) => {
        listAt(bucket).forEach((item) => {
          chips.push({
            id: `${d.key}:inc:${item}`,
            label: mapLabel(item),
            remove: removeFrom(bucket, item),
          })
        })
      })
      listAt('none').forEach((item) => {
        chips.push({
          id: `${d.key}:exc:${item}`,
          // Prefix conveys exclusion without relying on colour (≠ + "sauf").
          label: `≠ ${mapLabel(item)}`,
          ariaLabel: `Retirer le filtre : sauf ${mapLabel(item)}`,
          className: excludeChipClass,
          remove: removeFrom('none', item),
        })
      })
    } else if (d.type === 'range') {
      if (v && typeof v === 'object' && (v.gte != null || v.lte != null || v.gt != null || v.lt != null)) {
        // A `_label` baked into the range wins (human-readable tranche label,
        // e.g. "Avant 2020") ; otherwise format the numeric/date bounds.
        const lbl = v._label != null && v._label !== '' ? String(v._label) : rangeLabel(d, v)
        if (lbl) {
          chips.push({
            id: d.key,
            label: d.label ? `${d.label} : ${lbl}` : lbl,
            remove: () => globalDataContext.updateData({}, slotPath),
          })
        }
      }
    } else {
      // scalar
      if (v != null && v !== '' && v !== false) {
        chips.push({
          id: d.key,
          label: d.label ? `${d.label} : ${mapLabel(v)}` : mapLabel(v),
          remove: () => globalDataContext.updateData('', slotPath),
        })
      }
    }
  }

  if (chips.length === 0) return null

  const clearAll = () => {
    descriptors.forEach((d) => {
      globalDataContext.updateData(emptyFor(d.type), `${formDataPath}.${d.key}`)
    })
  }

  // A real filter change (chip removed / all cleared) : the store is updated
  // above, then we fire `activefilterschange` on the container. ActionDependant
  // has bound the YAML `actions:` (the refetch) to THAT event, so it runs only
  // here — never on a stray click in the wrapper's empty box.
  const notifyChange = () => {
    containerRef.current?.dispatchEvent(new CustomEvent('activefilterschange', { bubbles: true }))
  }

  return (
    <ActionDependant {...props} attributesHolderRef={containerRef}>
      <div ref={containerRef} className="flex gap-1.5 flex-wrap items-center">
        {chips.map((chip) => (
          <button
            key={chip.id}
            type="button"
            onClick={() => { chip.remove(); notifyChange() }}
            aria-label={chip.ariaLabel ?? `Retirer le filtre : ${chip.label}`}
            className={chip.className ?? chipClass}
          >
            {chip.label}
            <X className="w-3 h-3" />
          </button>
        ))}
        {clearAllLabel != null && (
          <button type="button" onClick={() => { clearAll(); notifyChange() }} className={clearAllClass}>
            {clearAllLabel}
          </button>
        )}
      </div>
    </ActionDependant>
  )
}
